import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import { LinkElement } from '@els/els-react--link-element';

import { isFirstInGroup as isFirstInGroupHelper, highlightQuery as highlightQueryHelper } from './AutosuggestInputHelper';

import './AutosuggestInputDropdown.scss';

function AutosuggestInputDropdown({ matches, groupBy, highlight, activeOptionIndex, listboxId, onActiveOptionChange, onSelect, query }) {
  useEffect(() => {
    if (activeOptionIndex < 0) {
      return;
    }

    const activeOption = document.getElementById(`${listboxId}-option-${activeOptionIndex}`);
    activeOption?.scrollIntoView?.({ block: 'nearest' });
  }, [activeOptionIndex, listboxId]);

  const isFirstInGroup = (index) => isFirstInGroupHelper({ matches, index, groupBy });

  const highlightMatch = (match) => highlightQueryHelper({ string: match.title, query });

  const getOptionLabel = (match) => (groupBy && match[groupBy] ? `${match[groupBy]}: ${match.title}` : undefined);

  return (
    <div
      data-e2e="e2e-quick-search-suggest-dropdown"
      className="react-autosuggest-input-dropdown react-autosuggest-input-dropdown--absolute">
      {/* eslint-disable jsx-a11y/click-events-have-key-events,react/no-array-index-key */}
      <ul
        id={listboxId}
        data-e2e="e2e-quick-search-suggest-list"
        className="e2e-quick-search-suggest-list react-autosuggest-input-dropdown__list"
        role="listbox"
        aria-label="Suggestions">
        {matches.map((match, index) => (
          <li
            aria-label={getOptionLabel(match)}
            aria-selected={activeOptionIndex === index ? 'true' : 'false'}
            id={`${listboxId}-option-${index}`}
            key={index}
            onMouseEnter={() => onActiveOptionChange(index)}
            onClick={() => {
              onSelect(index);
            }}
            role="option">
            {groupBy && isFirstInGroup(index) && (
              <div data-e2e="e2e-quick-search-suggest-list-category" className="react-autosuggest-input-dropdown__list__category">
                <h4>{match[groupBy]}</h4>
              </div>
            )}
            <LinkElement
              data-e2e="e2e-quick-search-suggest-list-item-link"
              href="null"
              navigation
              selected={activeOptionIndex === index}
              onClick={(e) => e.preventDefault()}
              tabIndex={-1}
              className="react-autosuggest-input-dropdown__list__link key-navigatable">
              <InnerHTML
                tag="span"
                className="react-autosuggest-input-dropdown__list__link__text"
                html={highlight ? highlightMatch(match) : match.title}
              />
            </LinkElement>
          </li>
        ))}
      </ul>
    </div>
  );
}

const MemoizedAutosuggestInputDropdown = React.memo(AutosuggestInputDropdown);

MemoizedAutosuggestInputDropdown.propTypes = {
  activeOptionIndex: PropTypes.number,
  listboxId: PropTypes.string.isRequired,
  query: PropTypes.string,
  matches: PropTypes.array.isRequired,
  onActiveOptionChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  groupBy: PropTypes.string,
  highlight: PropTypes.bool,
};

export default MemoizedAutosuggestInputDropdown;
